import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import keepAliveService from './services/keepAlive';
import './utils/keepAliveTest'; // Import for testing utilities
import { AuthProvider } from './hooks/useAuth';
import { DateProvider } from './contexts/DateContext';
import { GuestProvider } from './contexts/GuestContext';
import { SelectedCityProvider } from './contexts/SelectedCityContext';
import { ImageKitConfigProvider } from './contexts/ImageKitConfigContext';
import { StayCitiesProvider } from './contexts/StayCitiesContext';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import FloatingHelpButton from './components/FloatingHelpButton';
import LogoIntroOverlay from './components/LogoIntroOverlay';
import NotFoundRedirect from './components/NotFoundRedirect';
import AdminGate from './components/AdminGate';

// Pages
import Home from './pages/Home';
import Stays from './pages/Stays';
import Events from './pages/Events';
import Diary from './pages/Diary';
import PropertyDetail from './pages/PropertyDetail';
import RoomSelection from './pages/RoomSelection';
import BookingReview from './pages/BookingReview';
import Health from './pages/Health';
import Contact from './pages/Contact';
import Help from './pages/Help';
import FAQ from './pages/FAQ';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ReturnRefundCancellation from './pages/ReturnRefundCancellation';
import Vendor from './pages/Vendor';
import About from './pages/About';
import Offers from './pages/Offers';
import Account from './pages/Account';
import EventDetail from './pages/EventDetail';
import TicketSelection from './pages/TicketSelection';
import EventBookingReview from './pages/EventBookingReview';
import EventBookingConfirmation from './pages/EventBookingConfirmation';
import BookingConfirmation from './pages/BookingConfirmation';
import StayPaymentReturn from './pages/StayPaymentReturn';
import SearchResults from './pages/SearchResults';
import SetProfile from './pages/SetProfile';
import AllBookings from './pages/AllBookings';
import BookingDetail from './pages/BookingDetail';
import EventBookingDetail from './pages/EventBookingDetail';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStays from './pages/admin/AdminStays';
import AdminEvents from './pages/admin/AdminEvents';
import AdminRooms from './pages/admin/AdminRooms';
import AdminInventory from './pages/admin/AdminInventory';
import AdminBookings from './pages/admin/AdminBookings';

// Vendor Pages
import VendorLogin from './pages/vendor/VendorLogin';
import VendorLayout from './components/vendor/VendorLayout';
import ProtectedVendorRoute from './components/vendor/ProtectedVendorRoute';
import VendorPortalEntry from './components/vendor/VendorPortalEntry';
import VendorGrowth from './pages/vendor/VendorGrowth';
import VendorBookings from './pages/vendor/VendorBookings';
import VendorListings from './pages/vendor/VendorListings';
import VendorListingDetails from './pages/vendor/VendorListingDetails';
import VendorAccounts from './pages/vendor/VendorAccounts';
import VendorInventory from './pages/vendor/VendorInventory';
import { registerCheckoutAbandonListeners } from './utils/pendingStayBooking';

function AppContent() {
  const location = useLocation();

  useEffect(() => registerCheckoutAbandonListeners(), []);
  const isPropertyDetail = location.pathname.startsWith('/property/');
  const isEventDetail = location.pathname.startsWith('/event/');
  const isTicketSelection = location.pathname === '/ticket-selection' || location.pathname.startsWith('/ticket-selection/');
  const isEventBookingReview = location.pathname === '/event-booking-review';
  const isEventBookingConfirmation = location.pathname === '/event-booking-confirmation';
  const isRoomSelection = location.pathname.includes('/rooms');
  const isBookingReview = location.pathname.includes('/booking-review');
  const isBookingConfirmation = location.pathname === '/booking-confirmation';
  const isSearchResults = location.pathname === '/search-results';
  const isAdmin = location.pathname.startsWith('/admin');
  const isVendor = location.pathname.startsWith('/vendor');
  const isHelp = location.pathname === '/help';
  const isContact = location.pathname === '/contact';
  const isFAQ = location.pathname === '/faq';
  const isTerms = location.pathname === '/terms';
  const isPrivacy = location.pathname === '/privacy';
  const isReturnRefundCancellation = location.pathname === '/return-refund-cancellation';
  const isAbout = location.pathname === '/about';
  const isOffers = location.pathname === '/offers';
  const isAccount = location.pathname === '/account';
  const isDiary = location.pathname === '/diary';
  const isSetProfile = location.pathname === '/set-profile';
  const isAllBookings = location.pathname === '/all-bookings';
  const isBookingDetail = location.pathname === '/booking-detail';
  const isEventBookingDetail = location.pathname === '/event-booking-detail';

  return (
    <div className="App min-h-screen bg-[#faf7f2]">
      <ScrollToTop />
      {/* Vendor portal: no consumer Header (VendorLayout has its own chrome). For other routes, hide header on small screens only where listed. */}
      {!isVendor && (
        <div
          className={`${
            isPropertyDetail ||
            isEventDetail ||
            isTicketSelection ||
            isEventBookingReview ||
            isEventBookingConfirmation ||
            isBookingConfirmation ||
            isSearchResults ||
            isHelp ||
            isContact ||
            isFAQ ||
            isTerms ||
            isPrivacy ||
            isReturnRefundCancellation ||
            isAbout ||
            isAccount ||
            isSetProfile ||
            isAllBookings ||
            isBookingDetail ||
            isEventBookingDetail ||
            isOffers ||
            isAdmin
              ? 'hidden md:block'
              : ''
          }`}
        >
          <Header />
        </div>
      )}
      <LogoIntroOverlay />
      <main className="">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/stays" element={<Stays />} />
          <Route path="/search-results" element={<SearchResults />} />
          <Route path="/events" element={<Events />} />
          <Route path="/event/:id" element={<EventDetail />} />
          <Route path="/ticket-selection" element={<TicketSelection />} />
          <Route path="/ticket-selection/:eventId" element={<TicketSelection />} />
          <Route path="/event-booking-review" element={<EventBookingReview />} />
          <Route path="/event-booking-review/:id" element={<EventBookingReview />} />
          <Route path="/event-booking-confirmation" element={<EventBookingConfirmation />} />
          <Route path="/diary" element={<Diary />} />
          <Route path="/property/:id" element={<PropertyDetail />} />
          <Route path="/property/:id/rooms" element={<RoomSelection />} />
          <Route path="/property/:id/booking-review" element={<BookingReview />} />
          <Route path="/stay-payment-return" element={<StayPaymentReturn />} />
          <Route path="/booking-confirmation" element={<BookingConfirmation />} />
          <Route path="/health" element={<Health />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/return-refund-cancellation" element={<ReturnRefundCancellation />} />
          <Route path="/list-your-property" element={<Vendor />} />
          <Route path="/about" element={<About />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/account" element={<Account />} />
          <Route path="/blogs/*" element={<Navigate to="/" replace />} />
          <Route path="/set-profile" element={<SetProfile />} />
          <Route path="/all-bookings" element={<AllBookings />} />
          <Route path="/booking-detail" element={<BookingDetail />} />
          <Route path="/event-booking-detail" element={<EventBookingDetail />} />
          
          {/* Admin: must be signed in so JWT is sent (e.g. image upload) */}
          <Route path="/admin" element={<AdminGate />}>
            <Route index element={<AdminDashboard />} />
            <Route path="stay" element={<Navigate to="/admin/stays" replace />} />
            <Route path="stays" element={<AdminStays />} />
            <Route path="event" element={<Navigate to="/admin/events" replace />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="room" element={<Navigate to="/admin/rooms" replace />} />
            <Route path="rooms" element={<AdminRooms />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="booking" element={<Navigate to="/admin/bookings" replace />} />
            <Route path="bookings" element={<AdminBookings />} />
          </Route>

          {/* Vendor Routes */}
          <Route path="/vendor/login" element={<VendorLogin />} />
          <Route
            path="/vendor"
            element={
              <ProtectedVendorRoute>
                <VendorLayout />
              </ProtectedVendorRoute>
            }
          >
            <Route index element={<VendorPortalEntry />} />
            <Route path="growth" element={<VendorGrowth />} />
            <Route path="bookings" element={<VendorBookings />} />
            <Route path="listings" element={<VendorListings />} />
            <Route path="listings/:stayId" element={<VendorListingDetails />} />
            <Route path="accounts" element={<VendorAccounts />} />
            <Route path="inventory" element={<VendorInventory />} />
          </Route>

          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </main>
      {/* Hide footer on RoomSelection, BookingReview, BookingConfirmation, TicketSelection, EventBookingReview, EventBookingConfirmation, Diary, SetProfile, AllBookings, BookingDetail, EventBookingDetail, Admin, and Vendor pages */}
      {!isRoomSelection && !isBookingReview && !isBookingConfirmation && !isTicketSelection && !isEventBookingReview && !isEventBookingConfirmation && !isDiary && !isSetProfile && !isAllBookings && !isBookingDetail && !isEventBookingDetail && !isAdmin && !isVendor && <Footer />}
      
      {/* Floating Help Button - Show on all pages except admin and vendor */}
      {!isAdmin && !isVendor && <FloatingHelpButton />}
    </div>
  );
}

function App() {
  // Start keep-alive service when app loads
  useEffect(() => {
    // Start the keep-alive service
    keepAliveService.start();
    
    // Cleanup on unmount
    return () => {
      keepAliveService.stop();
    };
  }, []);

  return (
    <ImageKitConfigProvider>
      <AuthProvider>
        <DateProvider>
          <GuestProvider>
            <StayCitiesProvider>
              <SelectedCityProvider>
                <Router>
                  <AppContent />
                </Router>
              </SelectedCityProvider>
            </StayCitiesProvider>
          </GuestProvider>
        </DateProvider>
      </AuthProvider>
    </ImageKitConfigProvider>
  );
}

export default App; 